import React from 'react';
import { T, FONT_STACK } from '../customer/customers.theme';
import {
  Dialog,
  DialogHeader,
  EditIcon,
  ErrorLine,
  PrimaryButton,
  Spinner,
  TrashIcon,
} from '../customer/CustomersUI';
import { COPY } from './expenses.copy';

/**
 * The expense categories, managed in place.
 *
 * The phone's category sheet: one field at the top that both adds and renames,
 * and the list underneath with an edit and a delete on each row. It opens from
 * inside the expense form, so a category missing at the moment of filing can
 * be created without losing what has already been typed into that form.
 *
 * Deliberately not a tab in Administration: on the phone this lives with the
 * expenses, and a category here is only ever reached while entering one.
 */
export default function ExpenseCategoriesDialog({
  categories,
  onClose,
  onCreate,
  onRename,
  onDelete,
}) {
  const [name, setName] = React.useState('');
  const [editingId, setEditingId] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [busyRowId, setBusyRowId] = React.useState(null);
  const [error, setError] = React.useState('');

  const inputRef = React.useRef(null);

  const editing = Boolean(editingId);

  const cancelEdit = () => {
    setEditingId(null);
    setName('');
    setError('');
  };

  const submit = async () => {
    if (busy) return;

    const value = name.trim();
    if (!value) return setError(COPY.categoryNameRequired);

    setBusy(true);
    setError('');

    try {
      if (editingId) await onRename({ id: editingId, name: value });
      else await onCreate({ name: value });

      setEditingId(null);
      setName('');
    } catch (err) {
      setError(err?.response?.data?.message || COPY.saveFailed);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (category) => {
    if (busyRowId) return;

    setBusyRowId(category._id);
    setError('');

    try {
      await onDelete(category);
      // Deleting the row being renamed would leave the field editing a
      // category that is gone.
      if (editingId === category._id) cancelEdit();
    } catch (err) {
      setError(err?.response?.data?.message || COPY.saveFailed);
    } finally {
      setBusyRowId(null);
    }
  };

  const startEdit = (category) => {
    setEditingId(category._id);
    setName(category.name || '');
    setError('');
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={460}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader title={COPY.categoriesTitle} onClose={onClose} locked={busy} />

        <div style={{ display: 'flex', gap: 10, paddingTop: 18 }}>
          <input
            ref={inputRef}
            value={name}
            disabled={busy}
            autoFocus
            placeholder={COPY.categoryName}
            onChange={(event) => {
              setName(event.target.value);
              setError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit();
              }
              if (event.key === 'Escape' && editing) {
                event.preventDefault();
                event.stopPropagation();
                cancelEdit();
              }
            }}
            style={{
              flex: 1,
              minWidth: 0,
              height: 44,
              padding: '0 14px',
              background: T.surface,
              border: `1px solid ${T.borderStrong}`,
              borderRadius: T.radiusControl,
              font: 'inherit',
              fontFamily: FONT_STACK,
              fontSize: 14,
              color: T.ink,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />

          <PrimaryButton
            onClick={submit}
            busy={busy}
            style={{ height: 44, width: 92, flexShrink: 0 }}
          >
            {editing ? COPY.saveCategory : COPY.addCategory}
          </PrimaryButton>

          {editing ? (
            <button
              type="button"
              onClick={cancelEdit}
              disabled={busy}
              style={{
                height: 44,
                padding: '0 14px',
                flexShrink: 0,
                background: T.surface,
                border: `1px solid ${T.borderStrong}`,
                borderRadius: T.radiusControl,
                font: 'inherit',
                fontSize: 13.5,
                fontWeight: 600,
                color: T.inkSoft,
                cursor: 'pointer',
              }}
            >
              {COPY.cancel}
            </button>
          ) : null}
        </div>

        <div style={{ paddingTop: error ? 14 : 0 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            marginTop: 18,
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {categories.length ? (
            categories.map((category) => {
              const rowBusy = busyRowId === category._id;
              const beingEdited = editingId === category._id;

              return (
                <div
                  key={category._id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '11px 14px',
                    background: beingEdited ? T.selectedBg : T.sunk,
                    border: `1px solid ${beingEdited ? T.selectedBorder : T.border}`,
                    borderRadius: T.radiusControl,
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 14,
                      color: T.ink,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {category.name}
                  </span>

                  {rowBusy ? (
                    <span style={{ display: 'flex', color: T.inkFaint, paddingRight: 10 }}>
                      <Spinner size={15} />
                    </span>
                  ) : (
                    <React.Fragment>
                      <IconAction label="Edit" onClick={() => startEdit(category)}>
                        <EditIcon size={15} />
                      </IconAction>
                      <IconAction label={COPY.delete} danger onClick={() => remove(category)}>
                        <TrashIcon size={15} />
                      </IconAction>
                    </React.Fragment>
                  )}
                </div>
              );
            })
          ) : (
            <div
              style={{
                padding: '26px 0',
                textAlign: 'center',
                fontSize: 13,
                color: T.inkFaint,
              }}
            >
              {COPY.noCategories}
            </div>
          )}
        </div>

        <div style={{ fontSize: 11.5, color: T.inkFaint, lineHeight: 1.6, paddingTop: 14 }}>
          {COPY.deleteCategoryNote}
        </div>
      </div>
    </Dialog>
  );
}

function IconAction({ label, danger, onClick, children }) {
  const [hovered, setHovered] = React.useState(false);

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        flexShrink: 0,
        background: hovered ? (danger ? T.dangerTint : T.hover) : 'transparent',
        border: 'none',
        borderRadius: T.radiusControl,
        color: hovered ? (danger ? T.danger : T.ink) : T.inkGhost,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
